// captcha-broker.ts — 2026-05-28 plan §6 (fix-1):全局 captcha 请求队列。
//
// 背景:多处 uploader(upload.tsx / upload-button.tsx / profile/edit.tsx)都可能
// 收到后端 428 CAPTCHA_REQUIRED。最初只在 upload.tsx 挂 modal,导致其他入口
// 触发 428 后无 UI 接管 → 上传失败。
//
// 方案:模块级 FIFO 队列;全局只挂一个 <GlobalCaptchaGate /> 订阅 broker,
// 任何 uploader.onCaptchaRequired 未传时回落到 broker.request()。
//
// 串行语义:同一时刻 modal 只服务一个请求;后续请求排队等待 resolve / cancel。
//
// 2026-06-06 fix(多文件并发 captcha):
//   - 历史 bug:多文件并发上传时队列累积多个请求,服务完队首后第二个 captcha
//     "刷新不及时"失败。根因有二:
//       (Bug A)CaptchaModal 验证成功同时调 onVerified + onClose,gate 把 onClose
//              映射成 cancelHead → 误取消队列里"下一个文件"的请求。已在 modal 侧修。
//       (Bug B)gate 用 boolean `open` 驱动 modal,队首切到下一个请求时 open 不发生
//              false→true 跳变(被 React 批处理吃掉)→ useEffect 不重拉 challenge →
//              复用已消费的旧题 → 验证失败。
//   - 本文件新增 pending()/serial():gate 用 pending()>0 决定 open、用 serial() 当
//     CaptchaModal 的 key —— 队首每切到一个"新请求"serial 自增,key 变 → modal 重挂载
//     → 必拉一张新 challenge。并发入队(队首不变)不触发 serial 自增,不打断当前作答。

type Resolver = (token: string) => void
type Listener = () => void

const queue: Resolver[] = []
let listener: Listener | null = null
// serveSerial:每当"队首切换到一个新的待服务请求"自增(空队入队 / 出队后仍有剩余)。
// gate 把它当 modal key —— 变化即重挂载 modal,强制重新拉取 challenge(修 Bug B)。
let serveSerial = 0

export const captchaBroker = {
  // request 上传器调用:申请一个 captcha token,Promise 在用户完成验证或取消后 resolve
  // (cancel 时返空字符串,uploader 视为放弃 → 抛 UploadError)
  request(): Promise<string> {
    return new Promise<string>((resolve) => {
      const wasEmpty = queue.length === 0
      queue.push(resolve)
      if (wasEmpty) serveSerial += 1 // 空队 → 新队首开始被服务
      listener?.()
    })
  },

  // subscribe GlobalCaptchaGate 注册自身;broker 在队列变化时通知 UI 重新同步
  // (open = pending()>0,key = serial())。返回反订阅函数。
  subscribe(l: Listener): () => void {
    listener = l
    l() // 立即同步一次当前状态
    return () => {
      if (listener === l) listener = null
    }
  },

  // pending 当前排队中(含正在服务的队首)的请求数。
  pending(): number {
    return queue.length
  },

  // serial 队首"换人"的单调计数;gate 拿它当 modal key。
  serial(): number {
    return serveSerial
  },

  // resolve 验证码通过;消费队首,resolve 给 uploader。
  // 队列若还有请求,队首切到下一个 → serial 自增(gate 重挂载 modal 拉新题)。
  resolve(token: string): void {
    const next = queue.shift()
    if (next) next(token)
    if (queue.length > 0) serveSerial += 1
    listener?.()
  },

  // cancelHead 用户关闭 modal:队首 resolve 空字符串(uploader 视为放弃)。
  // 已经在排队的后续请求保留,队首切到下一个 → serial 自增,由 gate 接续弹出。
  cancelHead(): void {
    const next = queue.shift()
    if (next) next('')
    if (queue.length > 0) serveSerial += 1
    listener?.()
  },

  // 测试 util
  _resetForTest(): void {
    queue.length = 0
    listener = null
    serveSerial = 0
  },
}
